import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, Stack,
  Tab, Tabs, TextField, Tooltip, Typography, Switch, Alert,
} from '@mui/material';
import CloseIcon          from '@mui/icons-material/Close';
import CalendarMonthIcon  from '@mui/icons-material/CalendarMonth';
import EditIcon           from '@mui/icons-material/Edit';
import VisibilityIcon     from '@mui/icons-material/Visibility';
import RocketLaunchIcon   from '@mui/icons-material/RocketLaunch';
import OpenInNewIcon      from '@mui/icons-material/OpenInNew';
import AutoAwesomeIcon    from '@mui/icons-material/AutoAwesome';
import PeopleIcon         from '@mui/icons-material/People';
import TrendingUpIcon     from '@mui/icons-material/TrendingUp';
import AutoFixHighIcon    from '@mui/icons-material/AutoFixHigh';
import RichTextEditor     from '../massEmail/editor/RichTextEditor';
import { intelligenceApi } from '../../api/intelligenceApi';

const GREEN     = '#2db838';
const GREEN_DRK = '#1e9128';

const CAMPAIGN_TYPE_LABELS = {
  marketing:      'Marketing',
  follow_up:      'Follow-up',
  reminder:       'Reminder',
  sales_outreach: 'Sales Outreach',
  reactivation:   'Reactivation',
};

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function readableDateTime(isoLocal) {
  if (!isoLocal) return '';
  const d = new Date(isoLocal);
  return d.toLocaleString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
    year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Sidebar metadata panel ────────────────────────────────────────────────
function MetaSidebar({ suggestion, recommendation, scheduleAt, onScheduleAtChange, personalised, onPersonalisedChange }) {
  const priority = recommendation?.confidence_score >= 80
    ? { label: 'High Intent', color: GREEN }
    : recommendation?.confidence_score >= 65
      ? { label: 'Ready', color: '#b45309' }
      : { label: 'Standard', color: '#6b7280' };

  return (
    <Box sx={{ width: 260, flexShrink: 0, p: 3, borderRight: '1px solid #e5e7eb', overflowY: 'auto' }}>
      {/* AI badge */}
      <Stack direction="row" alignItems="center" spacing={0.75} mb={2}>
        <AutoAwesomeIcon sx={{ fontSize: 14, color: GREEN }} />
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: GREEN, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          AI Generated
        </Typography>
        {recommendation?.is_ai_generated && (
          <Chip label="Gemini" size="small" variant="outlined" sx={{ fontSize: 9, height: 18 }} />
        )}
      </Stack>

      {/* Audience */}
      <Box mb={2.5}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: 0.5, mb: 0.5 }}>
          AUDIENCE
        </Typography>
        <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3, mb: 0.5 }}>
          {suggestion?.audience_name}
        </Typography>
        <Stack direction="row" spacing={1.5} mt={0.75}>
          <Stack direction="row" alignItems="center" spacing={0.4}>
            <PeopleIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{suggestion?.estimated_size}</Typography>
            <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>contacts</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.4}>
            <TrendingUpIcon sx={{ fontSize: 14, color: GREEN }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: GREEN }}>
              {recommendation?.confidence_score}%
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      {/* Campaign meta */}
      <Box mb={2}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: 0.5, mb: 0.75 }}>
          CAMPAIGN TYPE
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.5}>
          <Chip
            label={CAMPAIGN_TYPE_LABELS[recommendation?.campaign_type] || recommendation?.campaign_type}
            size="small" color="primary" sx={{ fontWeight: 700 }}
          />
          <Box sx={{
            px: 0.9, py: 0.2, borderRadius: 0.75,
            bgcolor: '#f3f4f6', border: '1px solid #e5e7eb',
          }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: priority.color }}>
              {priority.label.toUpperCase()}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Theme */}
      {recommendation?.content_theme && (
        <Box mb={2}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: 0.5, mb: 0.5 }}>
            CONTENT THEME
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
            {recommendation.content_theme}
          </Typography>
        </Box>
      )}

      {/* Why this */}
      {recommendation?.reason_for_recommendation && (
        <Box mb={2.5}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: 0.5, mb: 0.5 }}>
            WHY THIS CAMPAIGN
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6, fontStyle: 'italic' }}>
            {recommendation.reason_for_recommendation}
          </Typography>
        </Box>
      )}

      <Divider sx={{ mb: 2.5 }} />

      {/* Schedule date/time picker */}
      <Box>
        <Stack direction="row" alignItems="center" spacing={0.5} mb={0.75}>
          <CalendarMonthIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: 0.5 }}>
            SEND DATE &amp; TIME
          </Typography>
        </Stack>
        <TextField
          type="datetime-local"
          size="small"
          fullWidth
          value={scheduleAt}
          onChange={(e) => onScheduleAtChange(e.target.value)}
          inputProps={{ step: 300 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: 13,
              '&.Mui-focused fieldset': { borderColor: GREEN },
            },
          }}
        />
        {scheduleAt && (
          <Typography sx={{ fontSize: 11, color: '#6b7280', mt: 0.5 }}>
            {readableDateTime(scheduleAt)}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2.5 }} />

      {/* Personalisation toggle */}
      <Box>
        <Stack direction="row" alignItems="center" spacing={0.5} mb={0.75}>
          <AutoFixHighIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: 0.5 }}>
            PERSONALISATION
          </Typography>
        </Stack>

        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1.5, p: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              size="small"
              checked={personalised}
              onChange={(e) => onPersonalisedChange(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: GREEN },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: GREEN },
              }}
            />
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
              {personalised ? 'Personalised' : 'One size fits all'}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5, lineHeight: 1.4 }}>
            {personalised
              ? 'AI rewrites the greeting for each contact using Groq.'
              : 'Same email sent to all contacts.'}
          </Typography>
        </Box>

        {personalised && (
          <Alert
            severity="info"
            icon={false}
            sx={{ mt: 1, py: 0.5, px: 1.25, fontSize: 11, '& .MuiAlert-message': { p: 0 } }}
          >
            A disclaimer is added to each personalised email stating the content was tailored using AI.
          </Alert>
        )}
      </Box>
    </Box>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────
export default function CampaignPreviewModal({
  open,
  suggestion,
  recommendation,
  onClose,
  onScheduled,
  onEditInMassEmail,
}) {
  const [tab,              setTab]              = useState(0);
  const [subject,          setSubject]          = useState('');
  const [bodyHtml,         setBodyHtml]         = useState('');
  const [scheduleAt,       setScheduleAt]       = useState('');
  const [scheduling,       setScheduling]       = useState(false);
  const [personalised,     setPersonalised]     = useState(false);
  const [signals,          setSignals]          = useState([]);
  const [loadingSignals,   setLoadingSignals]   = useState(false);
  const [expandedSignals,  setExpandedSignals]  = useState({});
  const [samples,          setSamples]          = useState([]);
  const [loadingSamples,   setLoadingSamples]   = useState(false);
  const [editedSamples,    setEditedSamples]    = useState({}); // { [index]: body_html }
  const [editingIndex,     setEditingIndex]     = useState(null);
  const iframeRef = useRef(null);

  const loadSignals = useCallback(async (suggestionId) => {
    if (!suggestionId) return;
    setLoadingSignals(true);
    try {
      const data = await intelligenceApi.getAudienceSignals(suggestionId);
      setSignals(data.signals ?? []);
    } catch { setSignals([]); }
    finally { setLoadingSignals(false); }
  }, []);

  const loadPersonalisedSamples = useCallback(async () => {
    if (!subject || !bodyHtml) return;
    setLoadingSamples(true);
    setSamples([]);
    setEditedSamples({});
    setEditingIndex(null);
    try {
      const data = await intelligenceApi.previewPersonalised({
        audience_suggestion_id: suggestion?.id,
        subject,
        body_html: bodyHtml,
        personalisation_mode: 'ai_generated',
      });
      setSamples(data.previews ?? []);
    } catch { setSamples([]); }
    finally { setLoadingSamples(false); }
  }, [subject, bodyHtml, suggestion?.id]);

  // Initialise from recommendation whenever modal opens
  useEffect(() => {
    if (!open || !recommendation) return;
    setSubject(recommendation.suggested_subject || '');
    setBodyHtml(recommendation.draft_body_html  || '');
    setScheduleAt(
      recommendation.suggested_send_date
        ? formatDateTime(recommendation.suggested_send_date)
        : formatDateTime(new Date(Date.now() + 86400000).toISOString())
    );
    setTab(0);
    setPersonalised(false);
    setSignals([]);
    if (suggestion?.id) loadSignals(suggestion.id);
  }, [open, recommendation, suggestion?.id, loadSignals]);

  useEffect(() => {
    if (!open || !personalised || tab !== 0) return;
    if (samples.length > 0 || loadingSamples) return;
    loadPersonalisedSamples();
  }, [open, personalised, tab, samples.length, loadingSamples, loadPersonalisedSamples]);

  const handleSchedule = async () => {
    if (!recommendation?.id) return;
    setScheduling(true);
    try {
      // Build overrides array from any user edits, keyed by contact email
      const overrides = personalised && samples.length > 0
        ? samples
            .map((s, i) => editedSamples[i] != null ? { email: s.email, body_html: editedSamples[i] } : null)
            .filter(Boolean)
        : null;

      await onScheduled(
        recommendation.id, scheduleAt, subject, bodyHtml,
        personalised ? 'ai_generated' : 'none',
        overrides,
      );
      onClose();
    } finally {
      setScheduling(false);
    }
  };

  if (!suggestion || !recommendation) return null;

  const hasContent = !!bodyHtml;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '92vw',
          maxWidth: 1200,
          height: '90vh',
          maxHeight: 900,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ p: 0, flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" sx={{ px: 3, py: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={0.25}>
              <AutoAwesomeIcon sx={{ fontSize: 16, color: GREEN }} />
              <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#111827' }}>
                Review AI-Generated Campaign
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
              Review the email before scheduling · Audience: <strong>{suggestion.audience_name}</strong>
            </Typography>
          </Box>
          <IconButton onClick={() => onClose?.()} size="small" sx={{ ml: 1 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Subject line (always visible) */}
        <Box sx={{ px: 3, py: 1.5, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
              SUBJECT
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject line…"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: 14,
                  fontWeight: 600,
                  bgcolor: '#fff',
                  '&.Mui-focused fieldset': { borderColor: GREEN },
                },
              }}
            />
          </Stack>
        </Box>
      </DialogTitle>

      {/* ── Body ── */}
      <DialogContent sx={{ p: 0, display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <MetaSidebar
          suggestion={suggestion}
          recommendation={recommendation}
          scheduleAt={scheduleAt}
          onScheduleAtChange={setScheduleAt}
          personalised={personalised}
          onPersonalisedChange={setPersonalised}
        />

        {/* Main content area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Tab bar + personalisation toggle in same row */}
          <Box sx={{ borderBottom: '1px solid #e5e7eb', px: 2, bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                minHeight: 44,
                '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600 },
                '& .Mui-selected': { color: GREEN },
                '& .MuiTabs-indicator': { bgcolor: GREEN },
              }}
            >
              <Tab icon={<VisibilityIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Preview" />
              <Tab icon={<EditIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Edit Content" />
              <Tab icon={<AutoFixHighIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Source Emails" />
            </Tabs>

            {/* Personalisation toggle — always visible */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ pr: 1 }}>
              <Typography sx={{ fontSize: 12, color: personalised ? GREEN : '#9ca3af', fontWeight: 600 }}>
                {personalised ? 'Personalised (Groq)' : 'One size fits all'}
              </Typography>
              <Switch
                size="small"
                checked={personalised}
                onChange={(e) => setPersonalised(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: GREEN },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: GREEN },
                }}
              />
            </Stack>
          </Box>

          {/* Preview tab */}
          {tab === 0 && (
            <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#f3f4f6', p: 2 }}>
              {hasContent ? (
                <Box sx={{ maxWidth: 680, mx: 'auto' }}>

                  {/* ONE SIZE: just the base email */}
                  {!personalised && (
                    <Box sx={{
                      bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e7eb',
                      overflow: 'hidden', boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
                    }}>
                      <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>From: Abisiniya Travels &lt;info@abisiniya.com&gt;</Typography>
                            <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>To: {suggestion.estimated_size} recipients</Typography>
                          </Box>
                          <Chip label="One size fits all" size="small" sx={{ fontSize: 10 }} />
                        </Stack>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.75, color: '#111827' }}>{subject}</Typography>
                      </Box>
                      <iframe
                        ref={iframeRef}
                        srcDoc={bodyHtml}
                        title="Email preview"
                        style={{ width: '100%', minHeight: 460, border: 'none', display: 'block' }}
                        sandbox="allow-same-origin"
                      />
                    </Box>
                  )}

                  {/* PERSONALISED: show all per-contact emails, no base email */}
                  {personalised && (
                    <>
                      {/* Header row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <AutoFixHighIcon sx={{ fontSize: 16, color: GREEN }} />
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: GREEN }}>
                            Personalised Emails
                          </Typography>
                          {samples.length > 0 && (
                            <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                              — {samples.length} {samples.length === 1 ? 'contact' : 'contacts'} in this audience
                            </Typography>
                          )}
                          {Object.keys(editedSamples).length > 0 && (
                            <Chip
                              label={`${Object.keys(editedSamples).length} edited`}
                              size="small"
                              color="warning"
                              sx={{ fontSize: 10, height: 20 }}
                            />
                          )}
                        </Stack>
                        {samples.length > 0 && (
                          <Button
                            size="small"
                            onClick={loadPersonalisedSamples}
                            sx={{ fontSize: 11, textTransform: 'none', color: '#9ca3af' }}
                          >
                            Regenerate
                          </Button>
                        )}
                      </Box>

                      {/* Loading */}
                      {loadingSamples && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8, gap: 1.5,
                          bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e7eb' }}>
                          <CircularProgress size={20} sx={{ color: GREEN }} />
                          <Typography sx={{ fontSize: 13, color: '#6b7280' }}>
                            Generating personalised emails for each contact…
                          </Typography>
                        </Box>
                      )}

                      {/* No samples yet */}
                      {!loadingSamples && samples.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#fff', borderRadius: 2,
                          border: '1px solid #e5e7eb' }}>
                          <AutoFixHighIcon sx={{ fontSize: 32, color: '#d1d5db', mb: 1 }} />
                          <Typography sx={{ fontSize: 13, color: '#6b7280', mb: 2 }}>
                            No personalised emails loaded yet.
                          </Typography>
                          <Button
                            variant="contained"
                            onClick={loadPersonalisedSamples}
                            sx={{ bgcolor: GREEN, '&:hover': { bgcolor: GREEN_DRK }, textTransform: 'none', fontWeight: 600 }}
                          >
                            Generate Personalised Emails
                          </Button>
                        </Box>
                      )}

                      {/* All personalised email cards */}
                      {!loadingSamples && samples.length > 0 && (
                        <Stack spacing={2}>
                          {samples.map((sample, index) => {
                            const currentHtml = editedSamples[index] ?? sample.body_html ?? '';
                            const isEditing   = editingIndex === index;
                            const wasEdited   = editedSamples[index] != null;

                            return (
                              <Box
                                key={`${sample.email || 'contact'}-${index}`}
                                sx={{
                                  bgcolor: '#fff',
                                  borderRadius: 2,
                                  border: wasEdited ? `1px solid ${GREEN}88` : '1px solid #e5e7eb',
                                  overflow: 'hidden',
                                  boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
                                }}
                              >
                                {/* Card header */}
                                <Box sx={{ px: 2.5, py: 1.25, bgcolor: wasEdited ? '#f0fdf4' : '#f9fafb',
                                  borderBottom: `1px solid ${wasEdited ? '#d1fae5' : '#e5e7eb'}` }}>
                                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Box>
                                      <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                                          {sample.contact_name || `Contact ${index + 1}`}
                                        </Typography>
                                        {wasEdited && (
                                          <Chip label="Edited" size="small" color="success" sx={{ fontSize: 9, height: 18 }} />
                                        )}
                                      </Stack>
                                      <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>
                                        {sample.email || 'No email'}
                                      </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={0.5}>
                                      {wasEdited && (
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setEditedSamples(prev => { const n = { ...prev }; delete n[index]; return n; });
                                            setEditingIndex(null);
                                          }}
                                          sx={{ fontSize: 11, textTransform: 'none', color: '#9ca3af', minWidth: 'auto' }}
                                        >
                                          Reset
                                        </Button>
                                      )}
                                      <Button
                                        size="small"
                                        variant={isEditing ? 'contained' : 'outlined'}
                                        startIcon={isEditing ? null : <EditIcon sx={{ fontSize: 13 }} />}
                                        onClick={() => setEditingIndex(isEditing ? null : index)}
                                        sx={{
                                          fontSize: 11, textTransform: 'none', fontWeight: 600,
                                          ...(isEditing
                                            ? { bgcolor: GREEN, '&:hover': { bgcolor: GREEN_DRK }, color: '#fff' }
                                            : { borderColor: '#d1d5db', color: '#374151' }),
                                        }}
                                      >
                                        {isEditing ? 'Done' : 'Edit'}
                                      </Button>
                                    </Stack>
                                  </Stack>
                                </Box>

                                {/* Edit mode: HTML textarea */}
                                {isEditing && (
                                  <Box sx={{ p: 2, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                    <Typography sx={{ fontSize: 11, color: '#6b7280', mb: 1 }}>
                                      Edit the HTML source for this contact. Click Done to preview your changes.
                                    </Typography>
                                    <TextField
                                      multiline
                                      fullWidth
                                      minRows={8}
                                      maxRows={20}
                                      value={currentHtml}
                                      onChange={(e) => setEditedSamples(prev => ({ ...prev, [index]: e.target.value }))}
                                      inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                                      sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: GREEN } }}
                                    />
                                  </Box>
                                )}

                                {/* Preview iframe */}
                                <iframe
                                  srcDoc={currentHtml}
                                  title={`Email for ${sample.contact_name || `Contact ${index + 1}`}`}
                                  style={{ width: '100%', minHeight: 360, border: 'none', display: 'block', background: '#fff' }}
                                  sandbox="allow-same-origin"
                                />
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No email content generated yet.</Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Edit tab */}
          {tab === 1 && (
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <Box sx={{ maxWidth: 720, mx: 'auto' }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280', mb: 1.5 }}>
                  Edit the email body below. Switch to Preview to verify your changes before scheduling.
                </Typography>
                <RichTextEditor
                  value={bodyHtml}
                  onChange={setBodyHtml}
                  placeholder="Write your email here…"
                />
              </Box>
            </Box>
          )}

          {/* Source Emails tab */}
          {tab === 2 && (
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <Box sx={{ maxWidth: 860, mx: 'auto' }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280', mb: 2 }}>
                  These are the classified emails from contacts in this audience — the signals Gemini used to write this campaign.
                </Typography>

                {loadingSignals ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} sx={{ color: GREEN }} />
                  </Box>
                ) : signals.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, color: '#9ca3af' }}>
                    <Typography>No classified email signals found for this audience.</Typography>
                  </Box>
                ) : (
                  <Stack spacing={1.5}>
                    {signals.map((s, i) => (
                      <Box
                        key={i}
                        sx={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          p: 2,
                          bgcolor: '#fff',
                          '&:hover': { borderColor: '#d1d5db', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
                        }}
                      >
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1}>
                          <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                              {s.contact_name}
                            </Typography>
                            {s.contact_email && (
                              <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{s.contact_email}</Typography>
                            )}
                          </Box>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" justifyContent="flex-end">
                            {s.destination && (
                              <Chip label={s.destination} size="small" color="primary" variant="outlined" sx={{ fontSize: 10 }} />
                            )}
                            {s.service_type && (
                              <Chip label={s.service_type.replace('_', ' ')} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                            )}
                            {s.buying_readiness != null && (
                              <Chip
                                label={`Readiness ${s.buying_readiness}%`}
                                size="small"
                                sx={{
                                  fontSize: 10,
                                  bgcolor: s.buying_readiness >= 70 ? '#d1fae5' : s.buying_readiness >= 40 ? '#fef9c3' : '#f3f4f6',
                                  color:   s.buying_readiness >= 70 ? '#065f46' : s.buying_readiness >= 40 ? '#92400e' : '#374151',
                                }}
                              />
                            )}
                            {s.urgency && s.urgency !== 'low' && (
                              <Chip label={`Urgency: ${s.urgency}`} size="small" color="warning" sx={{ fontSize: 10 }} />
                            )}
                          </Stack>
                        </Stack>

                        {/* Actual email body — HTML if available, plain text fallback */}
                        {(s.message_html || s.message_text) && (
                          <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                            {s.message_html ? (
                              <iframe
                                srcDoc={s.message_html}
                                title={`Email from ${s.contact_name || s.contact_email}`}
                                style={{ width: '100%', minHeight: 300, border: 'none', display: 'block', background: '#fff' }}
                                sandbox="allow-same-origin"
                              />
                            ) : (
                              <Box sx={{ bgcolor: '#f9fafb', px: 1.5, py: 1 }}>
                                <Typography sx={{ fontSize: 12, color: '#374151', fontStyle: 'italic', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                  "{expandedSignals[i]
                                    ? s.message_text
                                    : s.message_text.slice(0, 300) + (s.message_text.length > 300 ? '…' : '')}"
                                </Typography>
                                {s.message_text.length > 300 && (
                                  <Button
                                    size="small"
                                    onClick={() => setExpandedSignals(prev => ({ ...prev, [i]: !prev[i] }))}
                                    sx={{ fontSize: 11, textTransform: 'none', color: GREEN, mt: 0.5, px: 0, minWidth: 'auto' }}
                                  >
                                    {expandedSignals[i] ? 'Show less' : `Show full message`}
                                  </Button>
                                )}
                              </Box>
                            )}
                          </Box>
                        )}

                        <Stack direction="row" spacing={2} mt={1}>
                          {s.intent && (
                            <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                              Intent: <strong>{s.intent.replace('_', ' ')}</strong>
                            </Typography>
                          )}
                          {s.sentiment && (
                            <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                              Sentiment: <strong>{s.sentiment}</strong>
                            </Typography>
                          )}
                          {s.travel_date_hint && (
                            <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                              Travel date: <strong>{s.travel_date_hint}</strong>
                            </Typography>
                          )}
                          {s.classified_at && (
                            <Typography sx={{ fontSize: 11, color: '#9ca3af', ml: 'auto !important' }}>
                              {new Date(s.classified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e5e7eb', gap: 1, justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            onClick={() => onClose?.()}
            sx={{ textTransform: 'none', color: '#6b7280' }}
          >
            Cancel
          </Button>

          {onEditInMassEmail && (
            <Tooltip title="Open in Mass Email composer for full editing">
              <Button
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() => onEditInMassEmail?.()}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#d1d5db',
                  color: '#374151',
                  '&:hover': { borderColor: '#9ca3af' },
                }}
              >
                Edit in Mass Email
              </Button>
            </Tooltip>
          )}
        </Stack>

        <Button
          variant="contained"
          size="large"
          startIcon={scheduling ? <CircularProgress size={16} color="inherit" /> : <RocketLaunchIcon />}
          onClick={handleSchedule}
          disabled={scheduling || !scheduleAt}
          sx={{
            bgcolor: GREEN,
            '&:hover': { bgcolor: GREEN_DRK },
            fontWeight: 700,
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            fontSize: 14,
          }}
        >
          {scheduling ? 'Scheduling…' : personalised ? '✦ Schedule Personalised →' : 'Schedule Campaign →'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
